import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, UserRound } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { tabPanelId } from '@/components/ui/Tabs';
import { ACTIVITY_TYPE_LABEL } from '@/features/teacher/activities/constants';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { downloadCsv } from '@/lib/download';
import { cn } from '@/lib/utils';
import type { PaginationMeta } from '@/types/api';
import { parentApi } from '../api/parent.api';
import { TaskCalendarCard } from '../components/homework/TaskCalendarCard';
import { TaskTable, type TaskSortDirection } from '../components/homework/TaskTable';
import { UpcomingDeadlines } from '../components/homework/UpcomingDeadlines';
import { WorkDetails } from '../components/homework/WorkDetails';
import { WorkList } from '../components/homework/WorkList';
import { WorkStatRow, type WorkCounts } from '../components/homework/WorkStatRow';
import {
  DUE_OPTIONS,
  EMPTY_FILTERS,
  STATUS_CHIPS,
  WorkToolbar,
  isFiltered as hasFilters,
  type WorkDueFilter,
  type WorkFilterState,
  type WorkStatusFilter,
} from '../components/homework/WorkToolbar';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';
import type {
  ParentActivityRow,
  ParentHomeworkRow,
  ParentTaskItem,
  ParentTaskPriority,
  ParentWorkItem,
  ParentWorkType,
} from '../types/parent';
import {
  TASK_PRIORITY_LABEL,
  WORK_STATE_LABEL,
  WORK_TYPE_LABEL,
  formatDueDay,
  formatLongDay,
  isAssignedThisMonth,
  isDueThisWeek,
  todayIso,
  workState,
} from '../utils';

/**
 * Homework & Tasks, for the parent of the child on screen.
 *
 * Two tabs over one set of facts, drawn two ways because they are read two
 * ways. Homework is read *down* — a parent opens one piece of work and reads
 * what was set, so it is a list with the whole thing beside it. Tasks are read
 * *across* — six rows compared on priority and date to decide what happens this
 * evening — so they are a table, with the month and the next deadlines beside
 * it. The tiles, the filters and the export are shared; only the body differs.
 *
 * Both tabs are live. Homework reads `GET /parent/children/:uuid/homework` —
 * the everyday piece, from `homework`. Tasks reads
 * `GET /parent/children/:uuid/tasks` — the longer pieces a school sets, from
 * `class_activities`, each carrying the priority the teacher put on it. The two
 * are different tables and different shapes, which is why they are two calls
 * and two mappers rather than one endpoint switching on a parameter.
 *
 * The child is never in the address. It comes from the switcher in the sidebar,
 * and the server resolves which children the caller may see from their own
 * token — an id in the URL is an id somebody can change (CLAUDE.md §10).
 */

/** Which tab is open. The keys are the mock's, and will be the API's. */
type WorkView = 'homework' | 'tasks';

/** Rows per page. Six fills the panel beside the details without scrolling it. */
const PAGE_SIZE = 6;

/**
 * The list's filters and page, as they sit in the address bar.
 *
 * `WorkFilterState` with the picked day as `''` rather than `null`, because an
 * address can only hold text. The tab is not here: it was in the address
 * already, as `view`.
 */
type Filters = {
  page: number;
  status: WorkStatusFilter;
  subject: string;
  type: ParentWorkType | 'all';
  priority: ParentTaskPriority | 'all';
  due: WorkDueFilter;
  dueOn: string;
};

const DEFAULT_FILTERS: Filters = { page: 1, ...EMPTY_FILTERS, dueOn: '' };

/**
 * What a link may set. Subjects are the child's own and change with the
 * child, so any name is let through — one that matches nothing shows the empty
 * list with its "clear filters" button.
 */
const FILTER_OPTIONS = {
  allowed: {
    status: STATUS_CHIPS.map((chip) => chip.value),
    due: DUE_OPTIONS.map((option) => option.value),
    type: Object.keys(WORK_TYPE_LABEL),
    priority: Object.keys(TASK_PRIORITY_LABEL),
  },
};

/** What one row is called, in each tab. Capitalised for the tiles' labels. */
const NOUN: Record<WorkView, string> = {
  homework: 'Assignments',
  tasks: 'Tasks',
};

/**
 * Every kind of work `/parent/children/:uuid/homework` can return.
 *
 * One, today: the endpoint reads the `homework` table, and a school's projects
 * and practicals are `class_activities`, which this tab does not ask for. Named
 * here rather than written inline at both places it is needed — the row mapper
 * and the toolbar's filter — so adding the second kind is one edit, not two.
 */
const HOMEWORK_TYPES: ParentWorkType[] = ['HOMEWORK'];

/**
 * Every kind of work `/parent/children/:uuid/tasks` can return — the activity
 * kinds a school sets, each under its own name. `HOMEWORK` is not among them:
 * that is the other table.
 */
const TASK_TYPES = Object.keys(ACTIVITY_TYPE_LABEL) as ParentActivityRow['activityType'][];

/**
 * One row from the server, as this screen reads it.
 *
 * The teacher types the instructions into a single box, so what comes back is
 * one block of text. Its first line is the description and the rest are the
 * points beneath it — that is the structure the teacher wrote with their own
 * newlines, not one invented here. Work written as a single paragraph stays a
 * single paragraph, with no bullets under it.
 */
function toWorkItem(row: ParentHomeworkRow): ParentWorkItem {
  const [description = '', ...instructions] = row.instructions
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    uuid: row.uuid,
    subject: row.subject.name,
    title: row.title,
    type: 'HOMEWORK',
    assignedDate: row.assignedOn,
    dueDate: row.dueDate,
    description,
    instructions,
    attachment: row.attachment,
    /**
     * The day it was handed in, or null. `workState` only asks whether there is
     * anything here, and the details panel prints the day — neither needs a
     * time, and the column does not carry one.
     */
    submittedAt: row.submission?.handedInOn ?? null,
    submissionFileName: row.submission?.attachmentName ?? null,
    teacher: row.assignedBy,
  };
}

/**
 * One activity, as this screen reads it.
 *
 * The teacher writes an objective and, separately, how it must be handed in.
 * The objective is the description; the hand-in note becomes the points
 * beneath, split on the teacher's own newlines. An activity explained in class
 * carries no note and so shows no bullets.
 */
function toTaskItem(row: ParentActivityRow): ParentTaskItem {
  const instructions = (row.instructions ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    uuid: row.uuid,
    subject: row.subject.name,
    title: row.title,
    type: row.activityType,
    priority: row.priority,
    assignedDate: row.assignedOn,
    dueDate: row.dueDate,
    description: row.description,
    instructions,
    attachment: row.attachment,
    /**
     * The day it was handed in, or null. A physical hand-in — a model on the
     * desk — has no `submittedAt`, so the row's own day stands in: what matters
     * here is whether it arrived, not down to the minute.
     */
    submittedAt: row.submission?.handedInOn ?? null,
    submissionFileName: row.submission?.attachmentName ?? null,
    teacher: row.assignedBy,
  };
}

export function ParentHomeworkPage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);
  const [searchParams, setSearchParams] = useSearchParams();
  const [urlFilters, setUrlFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { page } = urlFilters;
  const filters = useMemo<WorkFilterState>(
    () => ({
      status: urlFilters.status,
      subject: urlFilters.subject,
      type: urlFilters.type,
      priority: urlFilters.priority,
      due: urlFilters.due,
      dueOn: urlFilters.dueOn || null,
    }),
    [urlFilters],
  );
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<TaskSortDirection>('asc');
  const detailsRef = useRef<HTMLElement>(null);

  /**
   * Today, read once per mount.
   *
   * Every derived figure on the screen is measured against it, so reading the
   * clock in each of them would let the tiles and the rows disagree about which
   * day it is on a tab left open across midnight.
   */
  const today = useMemo(() => todayIso(), []);

  /**
   * The open tab lives in the address, not in state alone.
   *
   * A tab held in state is a screen nobody can link to or reload — a parent
   * sent "look at the tasks" would land on the homework list instead.
   */
  const view: WorkView = searchParams.get('view') === 'tasks' ? 'tasks' : 'homework';
  const isTasks = view === 'tasks';
  const noun = NOUN[view];

  const setView = (next: WorkView) => {
    // `replace`, so paging back through four tab clicks does not walk back
    // through four history entries. Only `view` is touched — the filters sit
    // beside it in the address and carry across tabs, as they always have.
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'homework') params.delete('view');
        else params.set('view', next);
        return params;
      },
      { replace: true },
    );
  };

  /**
   * The child's homework, refetched whenever the switcher changes child.
   *
   * Everything on the screen narrows this one list in the browser rather than
   * asking again: a term of work is a short list the server already caps, and a
   * round trip per filter chip would be slower than the filtering it replaced.
   */
  const {
    data,
    isLoading: isLoadingHomework,
    error: homeworkError,
    refetch: refetchHomework,
  } = useApiQuery(
    () => (child ? parentApi.homework(child.uuid) : Promise.resolve(null)),
    [child?.uuid],
  );

  const homework = useMemo(() => (data?.homework ?? []).map(toWorkItem), [data]);

  /**
   * The child's activities — the Tasks tab.
   *
   * Asked for only while that tab is open: most visits are to the homework
   * list, and a second round trip on every child switch would be paid by
   * everybody to serve the tab nobody opened.
   */
  const {
    data: taskData,
    isLoading: isLoadingTasks,
    error: tasksError,
    refetch: refetchTasks,
  } = useApiQuery(
    () => (child && isTasks ? parentApi.tasks(child.uuid) : Promise.resolve(null)),
    [child?.uuid, isTasks],
  );

  const tasks = useMemo(
    () => (taskData?.activities ?? []).map(toTaskItem),
    [taskData],
  );

  /** Everything set for this child in this tab, whatever the filters say. */
  const source: ParentWorkItem[] = isTasks ? tasks : homework;

  /** Whichever request the open tab is waiting on, and whatever it said. */
  const isLoadingTab = isTasks ? isLoadingTasks : isLoadingHomework;
  const tabError = isTasks ? tasksError : homeworkError;

  /** The tiles count everything for the child, never what the filters left. */
  const counts = useMemo<WorkCounts>(() => {
    const states = source.map((item) => workState(item, today));

    return {
      total: source.length,
      thisMonth: source.filter((item) => isAssignedThisMonth(item, today)).length,
      completed: states.filter((state) => state === 'COMPLETED').length,
      // Upcoming work is pending too — it is simply not urgent yet. Counting it
      // separately would leave a parent adding two tiles together to answer
      // "how much is still to do?".
      pending: states.filter((state) => state === 'PENDING' || state === 'UPCOMING').length,
      overdue: states.filter((state) => state === 'OVERDUE').length,
      dueThisWeek: source.filter((item) => isDueThisWeek(item, today)).length,
    };
  }, [source, today]);

  /** Only the subjects this child actually has work in. */
  const subjects = useMemo(
    () => [...new Set(source.map((item) => item.subject))].sort((a, b) => a.localeCompare(b)),
    [source],
  );

  /**
   * Everything except priority, which only tasks carry.
   *
   * One predicate for both tabs rather than two that have to be kept saying the
   * same thing — the four chips, three dropdowns and the calendar's day all
   * narrow the same way whichever body is drawing the rows.
   */
  const matchesFilters = useCallback(
    (item: ParentWorkItem): boolean => {
      const state = workState(item, today);

      if (filters.status === 'COMPLETED' && state !== 'COMPLETED') return false;
      if (filters.status === 'OVERDUE' && state !== 'OVERDUE') return false;
      if (filters.status === 'PENDING' && state !== 'PENDING' && state !== 'UPCOMING') {
        return false;
      }

      if (filters.subject !== 'all' && item.subject !== filters.subject) return false;
      if (filters.type !== 'all' && item.type !== filters.type) return false;

      if (filters.due === 'overdue' && state !== 'OVERDUE') return false;
      if (filters.due === 'week' && !isDueThisWeek(item, today)) return false;
      if (filters.due === 'month' && item.dueDate.slice(0, 7) !== today.slice(0, 7)) return false;

      if (filters.dueOn && item.dueDate !== filters.dueOn) return false;

      return true;
    },
    [filters, today],
  );

  /** The tasks table's rows: filtered, then sorted the way its header says. */
  const filteredTasks = useMemo(
    () =>
      tasks
        .filter(
          (task) =>
            matchesFilters(task) &&
            (filters.priority === 'all' || task.priority === filters.priority),
        )
        .sort((a, b) =>
          sortDirection === 'asc'
            ? a.dueDate.localeCompare(b.dueDate)
            : b.dueDate.localeCompare(a.dueDate),
        ),
    [tasks, matchesFilters, filters.priority, sortDirection],
  );

  /** The homework list's rows: filtered, soonest deadline first. */
  const filteredHomework = useMemo(
    () => homework.filter(matchesFilters).sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [homework, matchesFilters],
  );

  const filtered: ParentWorkItem[] = isTasks ? filteredTasks : filteredHomework;

  /**
   * The page being shown, clamped.
   *
   * Filtering down to one page while sitting on page three would otherwise
   * leave an empty list under a paginator that says there is nothing to page
   * to.
   */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;

  const meta: PaginationMeta = {
    page: safePage,
    limit: PAGE_SIZE,
    total: filtered.length,
    totalPages,
  };

  /**
   * What is open, in whichever way this tab opens things.
   *
   * On the Homework tab the first row of the page stands in, so the panel
   * beside the list is never an empty rectangle. On the Tasks tab nothing is
   * open until a row is clicked, because there it opens a dialog over the
   * table — one that appeared by itself would be a dialog nobody asked for.
   * The task is looked up in the whole list rather than the filtered one, so a
   * deadline opened from the rail opens even when the filters exclude it.
   */
  const openHomework =
    filteredHomework.find((item) => item.uuid === openUuid) ??
    filteredHomework[pageStart] ??
    null;
  const openTask = openUuid ? (tasks.find((task) => task.uuid === openUuid) ?? null) : null;

  // A different child, or a different tab, is a different list: the page
  // number and the opened row belong to the one being left behind. The first
  // child to arrive is not a switch, so a page restored from the address stays.
  const shownList = useRef({ child: child?.uuid, view });
  useEffect(() => {
    const previous = shownList.current;
    shownList.current = { child: child?.uuid, view };
    const isSameChild = previous.child === undefined || previous.child === child?.uuid;
    if (isSameChild && previous.view === view) return;
    setUrlFilters({ page: 1 });
    setOpenUuid(null);
  }, [child?.uuid, view, setUrlFilters]);

  const setPage = (next: number) => setUrlFilters({ page: next });

  const openWork = (uuid: string) => {
    setOpenUuid(uuid);

    // Stacked on a phone, the homework details sit under the whole list, so a
    // tap on a row would otherwise look like it did nothing at all. The tasks
    // tab opens a dialog, which needs no scrolling.
    if (!isTasks && window.matchMedia('(max-width: 1023px)').matches) {
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const applyFilters = (next: WorkFilterState) => {
    setUrlFilters({ ...next, dueOn: next.dueOn ?? '', page: 1 });
  };

  const clearFilters = () => applyFilters(EMPTY_FILTERS);

  /**
   * The rows on screen, saved as they are read.
   *
   * Written in the browser rather than asked of the server: these rows are
   * already here and already this parent's own child's, so there is no second
   * endpoint to authorise for it (see `lib/download`).
   */
  const downloadList = () => {
    if (!child || filtered.length === 0) return;

    const header = isTasks
      ? ['Subject', 'Task', 'Type', 'Priority', 'Set on', 'Due', 'Status', 'Teacher']
      : ['Subject', 'Title', 'Type', 'Set on', 'Due', 'Status', 'Teacher'];

    const rows = isTasks
      ? filteredTasks.map((task) => [
          task.subject,
          task.title,
          WORK_TYPE_LABEL[task.type],
          TASK_PRIORITY_LABEL[task.priority],
          formatDueDay(task.assignedDate),
          formatDueDay(task.dueDate),
          WORK_STATE_LABEL[workState(task, today)],
          task.teacher,
        ])
      : filteredHomework.map((item) => [
          item.subject,
          item.title,
          WORK_TYPE_LABEL[item.type],
          formatDueDay(item.assignedDate),
          formatDueDay(item.dueDate),
          WORK_STATE_LABEL[workState(item, today)],
          item.teacher,
        ]);

    downloadCsv(`${child.fullName.toLowerCase().replaceAll(' ', '-')}-${view}-${today}.csv`, [
      header,
      ...rows,
    ]);
  };

  // The switcher is still loading. Checked before the empty state below, which
  // would otherwise flash "no child linked" on every visit to the page.
  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-24 rounded-3xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  // No child on the account — the office has not linked one yet. Everything
  // below is about one child, so there is nothing truthful to draw until there
  // is one.
  if (!child) {
    return (
      <div className={cn('flex flex-col gap-5', PARENT_PALETTE)}>
        <div className="card">
          <EmptyState
            icon={<UserRound className="h-5 w-5" />}
            title="No child linked to your account yet"
            description="Your school office links each parent account to their children. Once that is done, everything set for them appears here."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
      {/* ---------------- Page Header & Tabs ---------------- */}
      <header className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Homework &amp; Tasks
            </h1>
            <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
              Everything set for {child.fullName} in {child.className}.
            </p>
          </div>

          {/* Today Indicator */}
          <div className="flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
            <CalendarDays className="h-4 w-4 text-slate-400 dark:text-slate-400" />
            <span>Today is {formatLongDay(today)}</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-6 border-b border-slate-200 dark:border-white/10 pb-0 overflow-x-auto">
          <button
            type="button"
            onClick={() => setView('homework')}
            className={`pb-3 text-base font-semibold transition-all relative cursor-pointer ${
              !isTasks
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <span>Homework</span>
            {!isTasks && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setView('tasks')}
            className={`pb-3 text-base font-semibold transition-all relative cursor-pointer ${
              isTasks
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <span>Tasks</span>
            {isTasks && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
            )}
          </button>
        </div>
      </header>

      <div
        role="tabpanel"
        id={tabPanelId(view)}
        aria-labelledby={`${view}-tab`}
        className="flex flex-col gap-5"
      >
        {/* Each tab waits on its own request and reports its own failure: the
            two read different tables, and an error on one must not blank the
            other. */}
        {tabError ? (
          <QueryError
            title={`Could not load ${child.fullName}'s ${isTasks ? 'tasks' : 'homework'}`}
            error={tabError}
            onRetry={isTasks ? refetchTasks : refetchHomework}
          />
        ) : isLoadingTab ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {Array.from({ length: 5 }, (_, index) => (
                <Skeleton key={index} className="h-24 rounded-3xl" />
              ))}
            </div>
            <Skeleton className="h-20 rounded-3xl" />
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
              <Skeleton className="h-96 rounded-3xl lg:col-span-7" />
              <Skeleton className="h-96 rounded-3xl lg:col-span-5" />
            </div>
          </>
        ) : (
          <>
        <WorkStatRow
          counts={counts}
          status={filters.status}
          due={filters.due}
          noun={noun}
          onStatus={(status: WorkStatusFilter) =>
            // A tile is a view of everything, so it clears the date filters it
            // would otherwise silently sit on top of.
            applyFilters({ ...filters, status, due: 'all', dueOn: null })
          }
          onDueThisWeek={() =>
            applyFilters({ ...filters, status: 'ALL', due: 'week', dueOn: null })
          }
        />

        <WorkToolbar
          filters={filters}
          onChange={applyFilters}
          subjects={subjects}
          // Each tab offers only the kinds its own table can return, so a
          // filter can never come up empty for a kind that is not there.
          types={isTasks ? TASK_TYPES : HOMEWORK_TYPES}
          middle={isTasks ? 'priority' : 'type'}
          onClear={clearFilters}
          onDownload={downloadList}
          canDownload={filtered.length > 0}
        />

        {isTasks ? (
          <>
            {/* The table leads; the month and the next deadlines sit beside it
                on a wide screen and under it on a narrow one. */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-9">
                <TaskTable
                  rows={filteredTasks.slice(pageStart, pageStart + PAGE_SIZE)}
                  meta={meta}
                  onPageChange={setPage}
                  today={today}
                  sortDirection={sortDirection}
                  onToggleSort={() =>
                    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
                  }
                  onOpen={openWork}
                  onClearFilters={clearFilters}
                  isFiltered={hasFilters(filters)}
                />
              </div>

              <div className="flex flex-col gap-5 lg:col-span-3">
                <TaskCalendarCard
                  items={tasks}
                  today={today}
                  selectedDay={filters.dueOn}
                  onPickDay={(dateIso) =>
                    // A second click on the day already chosen puts it back,
                    // which is what a reader expects of something that reads as
                    // pressed.
                    applyFilters({
                      ...filters,
                      dueOn: filters.dueOn === dateIso ? null : dateIso,
                      due: 'all',
                    })
                  }
                />

                <UpcomingDeadlines
                  items={tasks}
                  today={today}
                  onOpen={openWork}
                  onViewAll={() => {
                    clearFilters();
                    setSortDirection('asc');
                  }}
                />
              </div>
            </div>

            {/* Bottom Note Banner */}
            <div className="rounded-2xl sm:rounded-3xl border border-indigo-200/90 dark:border-slate-800 bg-indigo-50/80 dark:bg-slate-900 p-4 sm:px-5 sm:py-4 flex items-center gap-3.5 shadow-xs">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-indigo-600 text-white shadow-xs">
                <CalendarDays className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-indigo-950 dark:text-indigo-400">Note</h3>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">
                  Tasks are assigned by your child’s teachers. Please ensure timely completion.
                </p>
              </div>
            </div>

            {/* The whole task, over the table. A dialog rather than a third
                column: the table already uses the width, and a parent opens one
                task, reads it and goes back to the list. */}
            <Modal
              isOpen={Boolean(openTask)}
              onClose={() => setOpenUuid(null)}
              title={openTask?.title ?? 'Task'}
              description={openTask ? `${openTask.subject} · ${openTask.teacher}` : undefined}
              size="lg"
            >
              {openTask && (
                <WorkDetails item={openTask} child={child} today={today} noun="task" bare />
              )}
            </Modal>
          </>
        ) : (
          /* The list leads and the details follow it, which on a phone is also
             the order they are read in. */
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <WorkList
                items={filteredHomework.slice(pageStart, pageStart + PAGE_SIZE)}
                meta={meta}
                onPageChange={setPage}
                selectedUuid={openHomework?.uuid ?? null}
                onSelect={openWork}
                today={today}
                noun="assignments"
                onClearFilters={clearFilters}
                isFiltered={hasFilters(filters)}
              />
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-20">
              <WorkDetails
                ref={detailsRef}
                item={openHomework}
                child={child}
                today={today}
                noun="assignment"
              />
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
